import React, { useState, useEffect } from "react";


// Function to ensure the date is in the yyyy-MM-dd format using native JavaScript
const formatDateToInput = (date) => {
  if (!date) return "";

  // Check if the date is already in yyyy-MM-dd format, if so, return as is
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (regex.test(date)) {
    return date; // Already in yyyy-MM-dd format
  }

  // Try parsing the date in dd-MM-yyyy format and reformat it to yyyy-MM-dd
  const [day, month, year] = date.split("-");
  if (day && month && year) {
    // Return the date in yyyy-MM-dd format
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return ""; // If it's an invalid format, return an empty string
};





const DynamicTable = ({ tableName, columns, localStorageKey, onTableDataChange, rows, hasPreset = false,  defaultRowCount = 1, }) => {
 // Initialize rows from defaultRowCount, localStorage, or an empty row
 const [localRows, setLocalRows] = useState(() => {
  if (hasPreset) {
    return []; // Use preset rows if available
  }

  const storedRows = JSON.parse(localStorage.getItem(localStorageKey));


  if (storedRows) {
    return storedRows; // If rows are stored in localStorage, use them
  }

  if (rows && rows.length > 0) {
    return rows; // If rows are passed as props, use them
  }

  // Default: Create `defaultRowCount` empty rows
  return Array.from({ length: defaultRowCount }).map(() => 
    columns.reduce((acc, { key, defaultValue }) => {
      acc[key] = defaultValue || ""; // Add empty value or defaultValue to the row
      return acc;
    }, {})
  );
});

  // Update localRows when rows prop changes (e.g. preset selected)
  useEffect(() => {
    if (rows) {
      setLocalRows(rows); // Sync local state with updated rows
    }
  }, [rows]);

  // Update localStorage when localRows change
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(localRows));
  }, [localRows, localStorageKey]);
  

  // Handle row changes (add, remove, update)
  const handleRowChange = (action, index = null, field = null, value = null) => {
    const updatedRows = [...localRows];

    if (action === "remove" && index !== null && index > 0) { // Prevent removing first row (index 0)
      updatedRows.splice(index, 1); // Remove row at the specified index
    } else if (action === "add") {
      updatedRows.splice(index + 1, 0, columns.reduce((acc, { key, defaultValue }) => {
        acc[key] = defaultValue || ""; // Add an empty row at the specified index
        return acc;
      }, {}));
    } else if (action === "update" && index !== null && field) {
      // Update the field in the specified row
      // If the field is a date, format it to yyyy-MM-dd
      updatedRows[index][field] = (field === "dateFrom" || field === "tillDate") ? formatDateToInput(value) : value;
    }

    setLocalRows(updatedRows); // Update local rows
    onTableDataChange(updatedRows); // Notify parent about the changes
  };

  // Render input fields based on the column type
  const renderInputField = (type, value, onChange, options = []) => {
    switch (type) {
      case "date":
        return (
          <input
            type="date"
            value={formatDateToInput(value)} // Ensure the value is in yyyy-MM-dd format
            onChange={onChange}
          />
        );
      case "number":
        return <input type="number" value={value} onChange={onChange} />;
      case "dropdown":
        return (
          <select value={value} onChange={onChange}>
            <option value="">Select</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      case "checkbox":
        return <input type="checkbox" checked={value} onChange={onChange} />;
      default:
        return <input type="text" value={value} onChange={onChange} />;
    }
  };

  return (
    <div className="google_forms">
      <label>{tableName}</label>
      <table border="0">
        <thead>
          <tr>
            {columns.map((col) => <th key={col.key}>{col.header}</th>)}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {localRows.map((row, idx) => (
            <tr key={idx}>
              {columns.map((col) => (
                <td key={col.key}>
                  {renderInputField(col.type, row[col.key] || "", e => handleRowChange("update", idx, col.key, e.target.value), col.options)}
                </td>
              ))}
              <td>
                     {/* "Add" button */}
                <button
                  type="button"
                  onClick={() => handleRowChange("add", idx)} // Ensure idx is passed correctly
                >
                  Add
                </button>

                <button
                  type="button"
                  onClick={() => handleRowChange("remove", idx)}
                  disabled={idx === 0} // Disable removal for the first row
                >
                  Remove
                </button>
                
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DynamicTable;
